/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function annotatePdfLinks() {
  var FILE_TYPE_CLASS = 'link-file-type';
  var META_CLASS = 'js-pdf-link-meta';
  var ENHANCED_ATTR = 'data-pdf-link-enhanced';

  function getStrings() {
    var strings = (window.theme && window.theme.strings) || {};
    return {
      fileTypePdf: strings.fileTypePdf || '(PDF)',
      opensInNewTab: strings.opensInNewTab || 'opens in a new tab'
    };
  }

  function isPdfHref(href) {
    if (!href) return false;
    try {
      return /\.pdf$/i.test(new URL(href, window.location.href).pathname);
    } catch (e) {
      return /\.pdf(?:[?#]|$)/i.test(href);
    }
  }

  function accessibleName(link) {
    if (link.getAttribute('aria-label')) return link.getAttribute('aria-label');
    return (link.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function alreadyHasPdfCue(name) {
    return /\bpdf\b/i.test(name);
  }

  function alreadyHasNewTabCue(name) {
    return /opens in a new|new tab|new window/i.test(name);
  }

  function annotateLink(link) {
    if (!link || link.hasAttribute(ENHANCED_ATTR)) return;
    if (link.querySelector('.' + META_CLASS)) {
      link.setAttribute(ENHANCED_ATTR, 'true');
      return;
    }
    if (!isPdfHref(link.getAttribute('href') || link.href)) return;

    var name = accessibleName(link);
    var strings = getStrings();
    var addPdf = !alreadyHasPdfCue(name);
    var addNewTab = link.getAttribute('target') === '_blank' && !alreadyHasNewTabCue(name);
    if (!addPdf && !addNewTab) {
      link.setAttribute(ENHANCED_ATTR, 'true');
      return;
    }

    var meta = document.createElement('span');
    meta.className = META_CLASS;

    if (addPdf) {
      var fileType = document.createElement('span');
      fileType.className = FILE_TYPE_CLASS;
      fileType.textContent = ' ' + strings.fileTypePdf;
      meta.appendChild(fileType);
    }

    if (addNewTab) {
      var newTab = document.createElement('span');
      newTab.className = 'visually-hidden';
      newTab.textContent = ', ' + strings.opensInNewTab;
      meta.appendChild(newTab);
    }

    var titleEl = link.querySelector('.care-guide__title');
    if (titleEl) {
      titleEl.appendChild(meta);
    } else {
      link.appendChild(meta);
    }
    link.setAttribute(ENHANCED_ATTR, 'true');
  }

  function annotateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var links = scope.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i += 1) {
      annotateLink(links[i]);
    }
    if (scope.matches && scope.matches('a[href]')) annotateLink(scope);
  }

  function init() {
    annotateAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    annotateAll(event.target);
  });
  document.addEventListener('on:variant:change', function () {
    annotateAll(document);
  });
  document.addEventListener('on:quickbuy:after-open', function () {
    annotateAll(document);
  });
})();

(function dialogBackgroundInert() {
  if (window.Shopify && window.Shopify.designMode) return;

  function applyBackgroundInert(dialog) {
    if (!dialog || dialog._a11yInertSiblings) return;
    var marked = [];
    var node = dialog;
    while (node && node !== document.documentElement) {
      var parent = node.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (sibling) {
        if (sibling === node || sibling.inert) return;
        if (sibling.classList && sibling.classList.contains('js-overlay')) return;
        if (sibling.id === 'ae_oss' || sibling.id === 'ae_app') return;
        sibling.inert = true;
        marked.push(sibling);
      });
      node = parent;
    }
    dialog._a11yInertSiblings = marked;
  }

  function clearBackgroundInert(dialog) {
    if (!dialog || !dialog._a11yInertSiblings) return;
    dialog._a11yInertSiblings.forEach(function (el) {
      el.inert = false;
    });
    dialog._a11yInertSiblings = null;
  }

  function wrapDialog(name) {
    customElements.whenDefined(name).then(function () {
      var Ctor = customElements.get(name);
      if (!Ctor || Ctor.prototype._a11yInertWrapped) return;
      Ctor.prototype._a11yInertWrapped = true;
      var origOpen = Ctor.prototype.open;
      var origClose = Ctor.prototype.close;
      if (typeof origOpen === 'function') {
        Ctor.prototype.open = function () {
          var result = origOpen.apply(this, arguments);
          applyBackgroundInert(this);
          return result;
        };
      }
      if (typeof origClose === 'function') {
        Ctor.prototype.close = function () {
          clearBackgroundInert(this);
          return origClose.apply(this, arguments);
        };
      }
    });
  }

  wrapDialog('modal-dialog');
  wrapDialog('details-modal');
  wrapDialog('side-drawer');
})();

/* Focus native form success/error after page reload (autofocus is unreliable). */
(function focusFormStatus() {
  function run() {
    var error = document.querySelector('.alert.errors[role="alert"][tabindex="-1"]:not([hidden])');
    if (error) {
      error.focus();
      return;
    }
    var status = document.querySelector('.js-form-status[tabindex="-1"]');
    if (status) status.focus();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

/**
 * Mark Gmail/Noto emoji <img> tags in richtext as decorative.
 * Pasted email content often ships emoji as fonts.gstatic.com/notoemoji images
 * with an emoji character in alt (e.g. alt="‼️"), which AudioEye flags.
 */
(function markEmojiImagesDecorative() {
  var SELECTOR = 'img[src*="notoemoji"]';

  function decorate(img) {
    if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
    img.setAttribute('aria-hidden', 'true');
    img.setAttribute('role', 'presentation');
  }

  function mark(root) {
    if (!root) return;
    if (root.matches && root.matches(SELECTOR)) decorate(root);
    if (!root.querySelectorAll) return;
    var imgs = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < imgs.length; i++) decorate(imgs[i]);
  }

  function run() {
    mark(document);
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          if (nodes[j].nodeType === 1) mark(nodes[j]);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
